import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { parseHashParams } from '../../utils/hashRouter';
import { FaPlus, FaMinus, FaCheckCircle, FaHeart } from 'react-icons/fa';
import Barcode from 'react-barcode';

//
import {
  getCrossProductList,
  getProductDetail,
  getGroupVariants,
  getRelatedProductList,
  getProductSpecifications,
  getProductDescription,
} from '../../api/services/productService';
import { addCart, getCartList } from '../../api/services/cartService';
import { addRecentViewItem } from '../../redux/userInfo';
import { setUserCartList } from '../../redux/userCart';
//
import { Title, BodyText } from '../../components/ui';
import Layout from '../../components/Layout';
import { decodeHtmlForRendering } from '../../utils';
import ProductImageView from './components/ProductImageView';
import Breadcrumb from '../../components/Breadcrumb';
import SpecificationTable from './components/SpecificationTable';

import SliderView from '../../components/SliderView';
import ProductCard from '../../components/ProductCard';
import { addFavorites } from '../../api/services/favoritesService';

import { IoMailSharp } from 'react-icons/io5';
import { useGuestCheck } from '../../hooks/useGuestCheck';

import { createRoot } from 'react-dom/client';

const Container = styled.div`
  flex: 1;
  max-width: 100%;
  overflow-x: hidden;
`;

const MainContainer = styled.div`
  display: flex;
  flex: 1;
  box-sizing: border-box;
  width: 100%;
  max-width: 100%;
  gap: 60px;
  /* 우측 바이박스가 좌측(긴 Description) 높이에 맞춰 늘어나지 않도록 상단 정렬 */
  align-items: flex-start;
  /* Keep the product content centered with equal space on both sides. */
  padding: 20px 10% 60px;

  @media (max-width: 768px) {
    flex-direction: column;
    /* 모바일(세로 적층)에서는 자식이 전체 너비를 쓰도록 복원 */
    align-items: stretch;
    gap: 20px;
    padding: 15px 3% 30px;
  }

  @media (max-width: 480px) {
    gap: 15px;
    padding: 10px 2% 25px;
  }
`;

const ProductInfoView = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 0;
  min-width: 0;

  @media (max-width: 768px) {
    width: 100%;
    flex: none;
  }

  @media (max-width: 480px) {
    width: 100%;
  }
`;

const ProductInfoRow = styled.div`
  display: flex;
  min-width: 0;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const Details = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  margin-left: 40px;

  @media (max-width: 768px) {
    margin-left: 20px;
    margin-top: 20px;
  }

  @media (max-width: 480px) {
    margin-left: 10px;
    margin-top: 15px;
  }
`;

const RightSection = styled.div`
  display: flex;
  flex-direction: column;
  flex: 0 0 calc(30% - 18px);
  min-width: 0;
  align-self: stretch;

  @media (max-width: 768px) {
    flex: none;
    align-self: auto;
    margin-top: 0;
  }

  @media (max-width: 480px) {
    margin-top: 0;
  }
`;

const SpecificationSection = styled.div`
  margin-top: auto;
`;

const PriceSection = styled.div`
  display: flex;
  flex: 1;
  max-height: 50vh;
  flex-direction: column;
  padding: 8px 20px;
  border: 1px solid #cecece;
  border-radius: 10px;
  background: ${(props) => props.theme.colors.white};

  @media (max-width: 768px) {
    max-height: none;
    padding: 12px 15px;
  }

  @media (max-width: 480px) {
    padding: 8px 12px;
  }
`;

const StockStatus = styled.p`
  display: flex;
  font-size: ${(props) => props.theme.fontSizes.medium};
  color: ${(props) =>
    props.avaQty > 0
      ? props.theme.colors.primaryGreen
      : props.theme.colors.primaryRed};
  font-weight: bold;
  font-size: 20px;

  @media (max-width: 768px) {
    font-size: 18px;
  }

  @media (max-width: 480px) {
    font-size: 16px;
  }
`;

const InfoContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const UnitPrice = styled.div`
  display: flex;
  font-size: ${(props) => props.theme.fontSizes.medium};
  color: #000;
  font-weight: bold;

  @media (max-width: 768px) {
    font-size: 0.9rem;
  }

  @media (max-width: 480px) {
    font-size: 0.85rem;
  }
`;

const Price = styled.div`
  display: flex;
  margin-top: 5px;
  font-size: 24px;
  color: ${(props) => props.theme.colors.black};
  font-weight: bold;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }

  @media (max-width: 480px) {
    font-size: 1.3rem;
  }
`;

const PackCartonContainer = styled.div`
  display: flex;
  margin-top: 8px;
  gap: 20px;
  align-items: center;

  @media (max-width: 768px) {
    margin-top: 6px;
    gap: 15px;
  }

  @media (max-width: 480px) {
    margin-top: 5px;
    gap: 12px;
  }
`;

const PackInfo = styled.div`
  display: flex;
  font-size: 16px;
  color: #000000;
  font-weight: 500;

  @media (max-width: 768px) {
    font-size: 18px;
  }

  @media (max-width: 480px) {
    font-size: 17px;
  }
`;

const CartonInfo = styled.div`
  display: flex;
  font-size: 16px;
  color: #000000;
  font-weight: 500;

  @media (max-width: 768px) {
    font-size: 18px;
  }

  @media (max-width: 480px) {
    font-size: 17px;
  }
`;

const OrderMultiplesText = styled.div`
  display: flex;
  font-size: 14px;
  color: #1e1e1e;
  margin-top: 8px;

  @media (max-width: 768px) {
    font-size: 14px;
  }

  @media (max-width: 480px) {
    font-size: 13px;
  }
`;

const DimensionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
  align-items: flex-start;

  @media (max-width: 768px) {
    gap: 6px;
    margin-top: 6px;
  }

  @media (max-width: 480px) {
    gap: 5px;
    margin-top: 5px;
  }
`;

const DimensionItem = styled.div`
  display: flex;
  font-size: 12px;
  color: #1e1e1e;
  font-weight: 400;

  @media (max-width: 768px) {
    font-size: 11px;
  }

  @media (max-width: 480px) {
    font-size: 10px;
  }
`;

const AddCartContainer = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  justify-content: space-between;
  margin-top: 1.5rem;
  height: 34px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 20px;
    margin-top: 1rem;
    align-items: flex-start;
    height: auto;
  }

  @media (max-width: 480px) {
    gap: 15px;
    margin-top: 0.8rem;
    align-items: flex-start;
    height: auto;
  }
`;

const VariantSelector = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 12px;
`;

const VariantLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #1e1e1e;
`;

const VariantOptions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const VariantButton = styled.button`
  min-width: 56px;
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid
    ${(props) => (props.selected ? props.theme.colors.primaryBlue : '#cecece')};
  background: ${(props) =>
    props.selected ? props.theme.colors.primaryBlue : '#fff'};
  color: ${(props) => (props.selected ? '#fff' : '#1e1e1e')};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: ${(props) => props.theme.colors.primaryBlue};
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
    text-decoration: line-through;
    &:hover {
      border-color: #cecece;
    }
  }
`;

const AddToCartButton = styled.button`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  background: ${(props) => props.theme.colors.primaryBlue};
  color: ${(props) => props.theme.colors.white};
  border: none;
  border-radius: 5px;
  font-size: ${(props) => props.theme.fontSizes.medium};
  cursor: pointer;
  height: 100%;
  &:hover {
    opacity: 0.8;
  }

  &:disabled {
    background: #cccccc;
    cursor: not-allowed;
    opacity: 0.6;
    font-size: 0.75rem;
    &:hover {
      opacity: 0.6;
    }
  }

  @media (max-width: 768px) {
    width: 100%;
    font-size: 0.9rem;
    height: auto;
    &:disabled {
      font-size: 0.7rem;
    }
  }

  @media (max-width: 480px) {
    font-size: 0.85rem;
    height: auto;
    &:disabled {
      font-size: 0.65rem;
    }
  }
`;

const QuantitySelector = styled.div`
  display: flex;
  border-radius: 5px;
  background: ${(props) => props.theme.colors.subBackground};
  flex: 1;
  align-items: center;
  justify-content: space-between;
  height: 100%;

  @media (max-width: 768px) {
    width: 100%;
    max-width: 200px;
    height: auto;
  }

  @media (max-width: 480px) {
    max-width: 150px;
    height: auto;
  }
`;

const QuantityButton = styled.button`
  display: flex;
  align-items: center;
  padding: 10px;
  background: ${(props) => props.theme.colors.btnSubBackground};
  border: none;
  border-radius: 5px;
  cursor: pointer;
  &:hover {
    background: #ccc;
  }

  @media (max-width: 480px) {
    padding: 8px;
  }
`;

const ButtonContainer = styled.div`
  margin-top: 1rem;
  display: flex;
  align-items: center;
  justify-content: flex-start;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 8px;
    margin-top: 0.8rem;
  }

  @media (max-width: 480px) {
    gap: 6px;
    margin-top: 0.6rem;
  }
`;

const FavoritesButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  background: none;
  border: none;
  padding: 10px;
  font-size: 12px;
  border-radius: 10px;
  color: #666666;
  cursor: pointer;
  width: 100%;
  transition: all 0.2s ease;

  &:hover {
    background: #d6d6d6;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background: #f0f0f0;
  }

  @media (max-width: 768px) {
    font-size: 11px;
    padding: 8px;
  }

  @media (max-width: 480px) {
    font-size: 10px;
    padding: 6px;
  }
`;

const ContactContainer = styled.div`
  display: flex;
  flex: 1;
  max-height: 50vh;
  flex-direction: column;
  padding: 10px 30px;
  border: 1px solid #cecece;
  border-radius: 10px;
  background: ${(props) => props.theme.colors.white};
  margin-top: 10px;

  @media (max-width: 768px) {
    max-height: none;
    padding: 15px 20px;
  }

  @media (max-width: 480px) {
    padding: 10px 15px;
  }
`;

const ContactText = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  margin: 8px;
  font-size: 14px;

  @media (max-width: 768px) {
    font-size: 13px;
    margin: 8px;
  }

  @media (max-width: 480px) {
    font-size: 12px;
    margin: 6px;
    flex-direction: column;
    align-items: flex-start;
    gap: 3px;
  }
`;

const ResponsiveTitle = styled(Title)`
  max-width: 650px;
  width: 100%;

  @media (max-width: 768px) {
    margin-top: 20px !important;
    font-size: 1.5rem !important;
  }

  @media (max-width: 480px) {
    margin-top: 15px !important;
    font-size: 1.3rem !important;
  }
`;

const ResponsiveBodyText = styled(BodyText)`
  @media (max-width: 768px) {
    font-size: 0.9rem;
  }

  @media (max-width: 480px) {
    font-size: 0.85rem;
  }
`;

const ResponsiveSliderContainer = styled.div`
  @media (max-width: 768px) {
    margin-top: 1.5rem !important;
  }

  @media (max-width: 480px) {
    margin-top: 1rem !important;
  }
`;

const ResponsiveCrossSellContainer = styled.div`
  @media (max-width: 768px) {
    margin-top: 1.5rem !important;
    margin-bottom: 5vh !important;
  }

  @media (max-width: 480px) {
    margin-top: 1rem !important;
    margin-bottom: 3vh !important;
  }
`;

const DescriptionSection = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 30px;
  max-width: 900px;
  width: 100%;

  @media (max-width: 768px) {
    margin-top: 25px;
  }

  @media (max-width: 480px) {
    margin-top: 20px;
  }
`;

const DescriptionTitle = styled.h2`
  font-size: 24px;
  font-weight: bold;
  color: ${(props) => props.theme.colors.black};
  margin-bottom: 15px;

  @media (max-width: 768px) {
    font-size: 20px;
    margin-bottom: 12px;
  }

  @media (max-width: 480px) {
    font-size: 18px;
    margin-bottom: 10px;
  }
`;

const DescriptionText = styled.div`
  font-size: 16px;
  line-height: 1.8;
  color: #333;
  white-space: pre-wrap;
  word-wrap: break-word;
  overflow-wrap: break-word;

  @media (max-width: 768px) {
    font-size: 15px;
    line-height: 1.7;
  }

  @media (max-width: 480px) {
    font-size: 14px;
    line-height: 1.6;
  }

  /* Style for line breaks added by decodeHtmlForRendering */
  br {
    display: block;
    content: '';
    margin-top: 0.5em;
  }

  /* Ensure paragraphs have spacing */
  p {
    margin: 0.5em 0;
  }

  /* Style lists if they exist in the description */
  ul,
  ol {
    margin: 0.5em 0;
    padding-left: 2em;
  }

  li {
    margin: 0.25em 0;
  }
`;

const ShortDescriptionContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 20px;

  @media (max-width: 768px) {
    margin-top: 15px;
  }

  @media (max-width: 480px) {
    margin-top: 12px;
  }
`;

const ShortDescriptionText = styled.div`
  font-size: 15px;
  line-height: 1.7;
  color: #555;
  white-space: pre-wrap;
  word-wrap: break-word;
  overflow-wrap: break-word;

  @media (max-width: 768px) {
    font-size: 14px;
    line-height: 1.6;
  }

  @media (max-width: 480px) {
    font-size: 13px;
    line-height: 1.5;
  }

  /* Style for line breaks added by decodeHtmlForRendering */
  br {
    display: block;
    content: '';
    margin-top: 0.5em;
  }
`;

export default function ProductPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const userInfo = useSelector((state) => state.userInfo);
  const userCart = useSelector((state) => state.userCart);

  const { isGuestUser } = useGuestCheck();

  const [product, setProduct] = useState();
  const [relatedProductList, setRelatedProductList] = useState([]);
  const [crossProductList, setCrossProductList] = useState([]);
  const [productSpecifications, setProductSpecifications] = useState([]);
  const [productDescription, setProductDescription] = useState(null);
  const [variants, setVariants] = useState([]);
  const [quantity, setQuantity] = useState(1);
  const [isAddingToFavorites, setIsAddingToFavorites] = useState(false);
  const [isInFavorites, setIsInFavorites] = useState(false);
  const [isAddingToCart, setIsAddingToCart] = useState(false);

  useEffect(() => {
    getDbRelatedProductList();
    getDbCrossProductList();
    checkIfInFavorites();
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token === 'null') {
      navigate('/');
    } else {
      getDbProductDetail();
    }
  }, [location.search]);

  useEffect(() => {
    if (product) {
      checkIfInFavorites();
      getDbCartList(); // Load cart to check current quantities
    }
  }, [product]);

  // Also refresh cart when cart_list changes (e.g., after adding items)
  // This ensures quantity selector reflects current cart state
  useEffect(() => {
    if (product && userCart.cart_list) {
      // When cart updates, ensure quantity doesn't exceed remaining available
      const cartQty = userCart.cart_list
        .filter((item) => item.idStyle === product.idStyle)
        .reduce((sum, item) => sum + (item.totQty || 0), 0);
      const remainingQty = Math.max(0, product.avaQty - cartQty);

      if (quantity > remainingQty && remainingQty > 0) {
        // Adjust quantity to max available if current quantity exceeds it
        const maxValidQty =
          Math.floor(remainingQty / product.qtyPerUOM) * product.qtyPerUOM;
        if (maxValidQty >= product.qtyPerUOM) {
          setQuantity(maxValidQty);
        } else {
          setQuantity(product.qtyPerUOM);
        }
      } else if (remainingQty <= 0 && quantity > 0) {
        // If no items available, set to minimum
        setQuantity(product.qtyPerUOM);
      }
    }
  }, [userCart.cart_list, product, quantity]);

  const getDbProductDetail = async (idOverride) => {
    const { params } = parseHashParams();
    const idStyle = idOverride || params.get('id');
    const priceLev = '01'; // Always use price level 01

    const result = await getProductDetail(
      idStyle,
      userInfo.idCustomer,
      userInfo.idStore,
      priceLev,
      userInfo.userId, // webuser 접근 차단(숨김 카테고리 상품) 검사
    );
    const data = result.product;

    // 접근 차단되었거나 존재하지 않는 상품이면 홈으로
    if (!data) {
      window.location.href = '/';
      return;
    }

    const tagList = data.tags
      ? data.tags.split(',').filter((item) => item.trim() !== '')
      : [];
    data.tagList = tagList;
    data.category_list = [];
    data.uom = `${data.qtyPerUOM}/${(data.qtyPerUOM / data.qtyPerUOM).toFixed(1)}`;

    setProduct(data);
    setQuantity(data.qtyPerUOM);
    dispatch(addRecentViewItem(data));
    getDbProductSpecifications(idStyle);
    getDbProductDescription(idStyle);
    getDbGroupVariants(idStyle);
  };

  // Parent/Child Item: 같은 그룹의 size/diameter variant 목록 로드
  const getDbGroupVariants = async (idOverride) => {
    const { params } = parseHashParams();
    const idStyle = idOverride || params.get('id');
    try {
      const result = await getGroupVariants(idStyle, userInfo.idStore);
      setVariants(result?.variants || []);
    } catch (error) {
      console.error('Error fetching group variants:', error);
      setVariants([]);
    }
  };

  // variant(size/diameter) 선택 시 해당 idStyle 상세로 전환
  const handleVariantSelect = async (newIdStyle) => {
    if (!newIdStyle || (product && newIdStyle === product.idStyle)) return;
    navigate(`/product?id=${newIdStyle}`);
    await getDbProductDetail(newIdStyle);
  };

  const getDbCartList = async () => {
    if (userInfo.zipcode) {
      const result = await getCartList(
        userInfo.idCustomer,
        userInfo.zipcode,
        userInfo.idStore,
      );
      dispatch(setUserCartList({ cart_list: result.cartList }));
      return result.cartList || [];
    }
    return [];
  };

  const getDbRelatedProductList = async () => {
    const { params } = parseHashParams();
    const idStyle = params.get('id');
    const result = await getRelatedProductList(
      idStyle,
      userInfo.idCustomer,
      userInfo.idStore,
      '01', // Always use price level 01
    );

    setRelatedProductList(result.productList);
  };

  const getDbCrossProductList = async () => {
    const { params } = parseHashParams();
    const idStyle = params.get('id');
    const result = await getCrossProductList(
      idStyle,
      userInfo.idCustomer,
      userInfo.idStore,
      '01', // Always use price level 01
    );

    setCrossProductList(result.productList);
  };

  const getDbProductSpecifications = async (idOverride) => {
    const { params } = parseHashParams();
    const idStyle = idOverride || params.get('id');

    try {
      const result = await getProductSpecifications(
        idStyle,
        userInfo.idCustomer,
        userInfo.idStore,
      );

      if (result.is_success && result.specifications) {
        setProductSpecifications(result.specifications);
      } else {
        setProductSpecifications([]);
      }
    } catch (error) {
      console.error('Error fetching specifications:', error);
      setProductSpecifications([]);
    }
  };

  const getDbProductDescription = async (idOverride) => {
    const { params } = parseHashParams();
    const idStyle = idOverride || params.get('id');

    try {
      const result = await getProductDescription(idStyle);
      setProductDescription(result.description || null);
    } catch (error) {
      console.error('Error fetching product description:', error);
    }
  };

  const showCartAlert = (message) => {
    const container = document.createElement('div');
    document.body.appendChild(container);

    const root = createRoot(container);

    const close = () => {
      root.unmount();
      container.remove();
    };

    root.render(
      <div
        onClick={close}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(211, 211, 211, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999,
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'relative',
            width: '400px',
            padding: '2rem',
            background: '#fff',
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0,0,0,.2)',
          }}
        >
          <button
            onClick={close}
            style={{
              position: 'absolute',
              top: '8px',
              right: '8px',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              fontSize: '20px',
            }}
          >
            ×
          </button>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              textAlign: 'left',
            }}
          >
            <FaCheckCircle color='rgb(0, 153, 0)' size={32} />
            <span dangerouslySetInnerHTML={{ __html: message }} />
          </div>

          <button
            onClick={() => {
              window.location.href = '/cart';
            }}
            style={{
              marginTop: '20px',
              width: '100%',
              background: '#2b388f',
              color: '#fff',
              border: 'none',
              padding: '10px',
              cursor: 'pointer',
            }}
          >
            View Cart
          </button>
        </div>
      </div>,
    );

    setTimeout(close, 3000);
  };

  const handleAddToCart = async (e) => {
    e.stopPropagation();

    // Prevent multiple simultaneous add operations
    if (isAddingToCart) {
      return;
    }

    setIsAddingToCart(true);

    try {
      // First, refresh cart to get the latest state
      await getDbCartList();

      // Now check if adding this quantity would exceed available inventory
      const cartQty = getCartQuantity();
      const remainingQty = Math.max(0, product.avaQty - cartQty);

      if (quantity > remainingQty) {
        alert(
          `Cannot add ${quantity} items. Only ${remainingQty} items available.`,
        );
        return;
      }

      const { idStyle, color } = product;
      const result = await addCart(
        userInfo.idCustomer,
        idStyle,
        color[0],
        quantity,
      );

      const decodeHtml = (text) => {
        const txt = document.createElement('textarea');
        txt.innerHTML = text;
        return txt.value;
      };

      if (result.is_success) {
        // Refresh cart after successful add
        await getDbCartList();
        showCartAlert(`${decodeHtml(product.webTitle)}<br />added to cart!`);
      } else {
        alert(result.message || `Error while add to cart`);
      }
    } finally {
      setIsAddingToCart(false);
    }
  };

  const showFavoritesAlert = (message) => {
    const container = document.createElement('div');
    document.body.appendChild(container);

    const root = createRoot(container);

    const close = () => {
      root.unmount();
      container.remove();
    };

    root.render(
      <div
        onClick={close}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(211, 211, 211, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999,
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'relative',
            width: '400px',
            padding: '2rem',
            background: '#fff',
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0,0,0,.2)',
          }}
        >
          <button
            onClick={close}
            style={{
              position: 'absolute',
              top: '8px',
              right: '8px',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              fontSize: '20px',
            }}
          >
            ×
          </button>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              textAlign: 'left',
            }}
          >
            <FaCheckCircle color='rgb(0, 153, 0)' size={32} />
            <span dangerouslySetInnerHTML={{ __html: message }} />
          </div>

          <button
            onClick={() => {
              window.location.href = '/Favorites';
            }}
            style={{
              marginTop: '20px',
              width: '100%',
              background: '#2b388f',
              color: '#fff',
              border: 'none',
              padding: '10px',
              cursor: 'pointer',
            }}
          >
            View Favorites
          </button>
        </div>
      </div>,
    );

    setTimeout(close, 3000);
  };

  const handleFavorites = async () => {
    try {
      // Check if required user information is available
      if (!userInfo.idCustomer) {
        alert('Please log in to add items to favorites');
        return;
      }

      if (!userInfo.idStore) {
        alert('Store information is missing. Please try refreshing the page.');
        return;
      }

      setIsAddingToFavorites(true);
      const { idStyle, color, websitename } = product;

      // Handle color array - extract the actual color value
      let actualColor = color;
      if (Array.isArray(color)) {
        // If color is an array, use the first non-empty value
        actualColor =
          color.find((c) => c && c.toString().trim() !== '') || color[0] || '';
      }

      // Check if environment variables are set
      if (!process.env.REACT_APP_API_BASE_URL) {
        console.warn('API Base URL not set, using local storage fallback');
        // Fallback to local storage
        const localFavorites = JSON.parse(
          localStorage.getItem('localFavorites') || '[]',
        );
        const newFavorite = {
          idStyle,
          color: actualColor,
          quantity,
          websitename,
          addedAt: new Date().toISOString(),
        };

        // Check if already exists
        const exists = localFavorites.some((fav) => fav.idStyle === idStyle);
        if (!exists) {
          localFavorites.push(newFavorite);
          localStorage.setItem(
            'localFavorites',
            JSON.stringify(localFavorites),
          );
          setIsInFavorites(true);
          alert(`Added to local favorites (API unavailable): ${websitename}`);
        } else {
          alert(`${websitename} is already in your favorites`);
        }
        return;
      }

      const result = await addFavorites(
        userInfo.idCustomer,
        idStyle,
        actualColor,
        quantity,
        userInfo.idStore,
      );

      if (result.is_success || result.success) {
        setIsInFavorites(true);
        showFavoritesAlert(`Added to favorites<br />${websitename}`);
      } else {
        const errorMessage =
          result.message || result.error || 'Unknown error occurred';
        alert(`Error adding to favorites: ${errorMessage}`);
      }
    } catch (error) {
      console.error('Error adding to favorites:', error);

      // If API fails, fallback to local storage
      if (
        error.message.includes('Network Error') ||
        error.code === 'ERR_NETWORK'
      ) {
        const { idStyle, color, websitename } = product;

        // Handle color array for fallback as well
        let actualColor = color;
        if (Array.isArray(color)) {
          actualColor =
            color.find((c) => c && c.toString().trim() !== '') ||
            color[0] ||
            '';
        }

        const localFavorites = JSON.parse(
          localStorage.getItem('localFavorites') || '[]',
        );
        const newFavorite = {
          idStyle,
          color: actualColor,
          quantity,
          websitename,
          addedAt: new Date().toISOString(),
        };

        const exists = localFavorites.some((fav) => fav.idStyle === idStyle);
        if (!exists) {
          localFavorites.push(newFavorite);
          localStorage.setItem(
            'localFavorites',
            JSON.stringify(localFavorites),
          );
          setIsInFavorites(true);
          alert(`Added to local favorites (API unavailable): ${websitename}`);
        } else {
          alert(`${websitename} is already in your favorites`);
        }
      } else {
        alert(
          `Error adding to favorites: ${
            error.message || 'Network error occurred'
          }`,
        );
      }
    } finally {
      setIsAddingToFavorites(false);
    }
  };

  const checkIfInFavorites = () => {
    if (!product) return;

    // Check local favorites first
    const localFavorites = JSON.parse(
      localStorage.getItem('localFavorites') || '[]',
    );

    // Handle color array for comparison
    let productColor = product.color;
    if (Array.isArray(product.color)) {
      productColor =
        product.color.find((c) => c && c.toString().trim() !== '') ||
        product.color[0] ||
        '';
    }

    const isInLocalFavorites = localFavorites.some((fav) => {
      // Compare both idStyle and color for more accurate matching
      let favColor = fav.color;
      if (Array.isArray(fav.color)) {
        favColor =
          fav.color.find((c) => c && c.toString().trim() !== '') ||
          fav.color[0] ||
          '';
      }
      return fav.idStyle === product.idStyle && favColor === productColor;
    });

    if (isInLocalFavorites) {
      setIsInFavorites(true);
      return;
    }

    // TODO: Check server favorites if API is available
    // For now, just check local storage
    setIsInFavorites(false);
  };

  // Calculate current quantity of this product in cart
  const getCartQuantity = () => {
    if (!product || !userCart.cart_list) return 0;

    // Sum up all quantities for items with matching idStyle
    // We match by idStyle only to account for all variants/colors of the same product
    const cartQuantity = userCart.cart_list
      .filter((item) => item.idStyle === product.idStyle)
      .reduce((sum, item) => sum + (item.totQty || 0), 0);

    return cartQuantity;
  };

  // Calculate remaining available quantity (total available - already in cart)
  const getRemainingAvailableQty = () => {
    if (!product) return 0;
    const cartQty = getCartQuantity();
    return Math.max(0, product.avaQty - cartQty);
  };

  if (product) {
    return (
      <Layout>
        <Container>
          <MainContainer>
            <ProductInfoView>
              <ProductInfoRow>
                <ProductImageView product={product} />
                <Details>
                  {product.category_list.length > 0 ? (
                    <Breadcrumb itemList={product.category_list} />
                  ) : null}

                  <ResponsiveTitle
                    style={{ marginTop: '30px' }}
                    dangerouslySetInnerHTML={{ __html: product.websitename }}
                  />
                  <ResponsiveBodyText
                    style={{
                      // marginTop: '20px',
                      fontWeight: 'bold',
                      fontSize: '18px',
                    }}
                  >{`SKU : ${product.idStyle}`}</ResponsiveBodyText>
                  {product.barcode && (
                    <>
                      <ResponsiveBodyText>{`Barcode: ${product.barcode}`}</ResponsiveBodyText>
                      <div style={{ marginTop: '10px' }}>
                        <Barcode
                          value={
                            product.barcode.length === 12
                              ? `0${product.barcode}`
                              : product.barcode.length === 13
                                ? product.barcode
                                : `00${product.barcode}`
                          }
                          format='EAN13'
                          width={1.6}
                          height={64}
                          displayValue={true}
                          fontSize={10}
                          margin={4}
                        />
                      </div>
                    </>
                  )}
                  <DimensionsContainer style={{ marginTop: '20px' }}>
                    <DimensionItem>{`W x L x H: ${
                      (Array.isArray(productSpecifications) &&
                        productSpecifications.find((s) => s.code === 'PROD')
                          ?.width) ||
                      'N/A'
                    } x ${
                      (Array.isArray(productSpecifications) &&
                        productSpecifications.find((s) => s.code === 'PROD')
                          ?.length) ||
                      'N/A'
                    } x ${
                      (Array.isArray(productSpecifications) &&
                        productSpecifications.find((s) => s.code === 'PROD')
                          ?.height) ||
                      'N/A'
                    }`}</DimensionItem>
                    <DimensionItem>{`Weight: ${
                      (Array.isArray(productSpecifications) &&
                        productSpecifications.find((s) => s.code === 'PROD')
                          ?.weight) ||
                      'N/A'
                    }`}</DimensionItem>
                  </DimensionsContainer>

                  {productDescription && productDescription.shortDescr && (
                    <ShortDescriptionContainer>
                      <ShortDescriptionText
                        dangerouslySetInnerHTML={{
                          __html: decodeHtmlForRendering(
                            productDescription.shortDescr,
                          ),
                        }}
                      />
                    </ShortDescriptionContainer>
                  )}
                </Details>
              </ProductInfoRow>

              {productDescription && productDescription.fullDescr && (
                <DescriptionSection>
                  <DescriptionTitle>Details</DescriptionTitle>
                  <DescriptionText
                    dangerouslySetInnerHTML={{
                      __html: decodeHtmlForRendering(
                        productDescription.fullDescr,
                      ),
                    }}
                  />
                </DescriptionSection>
              )}
            </ProductInfoView>
            <RightSection>
              <PriceSection>
                <StockStatus avaQty={product.avaQty}>
                  {product.avaQty > 0 ? (
                    <FaCheckCircle style={{ marginRight: '5px' }} />
                  ) : null}
                  {product.avaQty > 0 ? 'In Stock' : 'Out of Stock'}
                </StockStatus>
                <InfoContainer>
                  <UnitPrice>{`SRP: $${product.retailPrice ? product.retailPrice.toFixed(2) : product.price} / UNIT`}</UnitPrice>
                  {!isGuestUser && (
                    <Price>{`$${(product.price - product.salePrice).toFixed(2)} / UNIT`}</Price>
                  )}
                  <PackCartonContainer>
                    <PackInfo>{`Pack: ${product.qtyPerUOM}`}</PackInfo>
                    <CartonInfo>{`Carton: ${product.qtyPerCTN}`}</CartonInfo>
                  </PackCartonContainer>
                  <OrderMultiplesText>
                    {`Must be ordered in multiples of ${product.qtyPerUOM}`}
                  </OrderMultiplesText>
                  {variants.length > 1 && (
                    <VariantSelector>
                      <VariantLabel>
                        {variants[0]?.optionType === 'DIAMETER'
                          ? 'Diameter'
                          : 'Size'}
                      </VariantLabel>
                      <VariantOptions>
                        {variants.map((v) => (
                          <VariantButton
                            key={v.idStyle}
                            selected={v.idStyle === product.idStyle}
                            disabled={v.avaQty <= 0}
                            title={
                              v.avaQty <= 0 ? 'Out of stock' : v.optionLabel
                            }
                            onClick={() => handleVariantSelect(v.idStyle)}
                          >
                            {v.optionLabel}
                          </VariantButton>
                        ))}
                      </VariantOptions>
                    </VariantSelector>
                  )}
                  <AddCartContainer>
                    <QuantitySelector>
                      <QuantityButton
                        disabled={
                          quantity - product.qtyPerUOM <= 0 ||
                          getRemainingAvailableQty() <= 0 ||
                          isGuestUser
                        }
                        onClick={() => {
                          setQuantity(quantity - product.qtyPerUOM);
                        }}
                      >
                        <FaMinus />
                      </QuantityButton>
                      <span style={{ margin: '0 10px' }}>{quantity}</span>
                      <QuantityButton
                        disabled={
                          getRemainingAvailableQty() <= 0 ||
                          isGuestUser ||
                          quantity + product.qtyPerUOM >
                            getRemainingAvailableQty()
                        }
                        onClick={async () => {
                          // Refresh cart to get latest state before checking
                          await getDbCartList();
                          const newQuantity = quantity + product.qtyPerUOM;
                          const remainingQty = getRemainingAvailableQty();
                          if (newQuantity <= remainingQty) {
                            setQuantity(newQuantity);
                          } else {
                            alert(
                              `Cannot increase quantity beyond available inventory. Only ${remainingQty} items remaining.`,
                            );
                          }
                        }}
                      >
                        <FaPlus />
                      </QuantityButton>
                    </QuantitySelector>
                    <AddToCartButton
                      onClick={handleAddToCart}
                      disabled={
                        getRemainingAvailableQty() <= 0 ||
                        isGuestUser ||
                        isAddingToCart
                      }
                    >
                      {isGuestUser
                        ? 'GUEST - CART DISABLED'
                        : isAddingToCart
                          ? 'Adding...'
                          : 'Add To Cart'}
                    </AddToCartButton>
                  </AddCartContainer>
                </InfoContainer>
                <ButtonContainer>
                  <FavoritesButton
                    onClick={handleFavorites}
                    disabled={isAddingToFavorites}
                    style={{
                      color: isInFavorites ? '#e74c3c' : '#666666',
                      background: isInFavorites ? '#ffe6e6' : 'none',
                    }}
                  >
                    <FaHeart
                      style={{
                        marginRight: '5px',
                        color: isInFavorites ? '#e74c3c' : '#666666',
                      }}
                    />
                    {isAddingToFavorites
                      ? 'Adding...'
                      : isInFavorites
                        ? 'In Favorites'
                        : 'Add To Favorites'}
                  </FavoritesButton>
                </ButtonContainer>
              </PriceSection>
              <ContactContainer>
                <ContactText>
                  For questions about freight, lead times, or custom orders,
                  please reach out to our team.
                </ContactText>
                <ContactText style={{ fontWeight: 'bold' }}>
                  <IoMailSharp />
                  <a href='mailto:orders@edencorp.com'>orders@edencorp.com</a>
                </ContactText>
              </ContactContainer>
              <SpecificationSection>
                <SpecificationTable
                  product={product}
                  productSpecifications={productSpecifications}
                />
              </SpecificationSection>
            </RightSection>
          </MainContainer>

          {relatedProductList.length > 0 && (
            <ResponsiveSliderContainer>
              <SliderView title={'Related Items'} style={{ marginTop: '2rem' }}>
                {relatedProductList.map((item, index) => {
                  return (
                    <div key={`product_related_${index}`}>
                      <ProductCard data={item} key={`product_${index}`} />
                    </div>
                  );
                })}
              </SliderView>
            </ResponsiveSliderContainer>
          )}

          {crossProductList.length > 0 && (
            <ResponsiveCrossSellContainer>
              <SliderView
                title={'Commonly Ordered Together'}
                style={{ marginTop: '2rem', marginBottom: '10vh' }}
              >
                {crossProductList.map((item, index) => {
                  return (
                    <div key={`product_cross_${index}`}>
                      <ProductCard data={item} key={`product_${index}`} />
                    </div>
                  );
                })}
              </SliderView>
            </ResponsiveCrossSellContainer>
          )}
        </Container>
      </Layout>
    );
  }
}
